import {
  BooleanOperator,
  isQueryBuilderRule,
  Operator,
  type QueryBuilderQuery,
  type QueryBuilderRule,
} from '@ardoq/api-types';
import {
  advancedSearchComponentNameAndTypeAndStartSetQuery,
  queryBuilderOperations,
} from '@ardoq/query-builder';
import type { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import type { SelectContextSearchMode } from './types';

export type SimpleSearchFilters = {
  componentName: string;
  componentType: string;
};

export type CarriedSearchState =
  | { mode: 'advanced'; query: QueryBuilderQuery | null }
  | { mode: 'simple'; filters: SimpleSearchFilters };

const emptySimpleSearchFilters: SimpleSearchFilters = {
  componentName: '',
  componentType: '',
};

export const isAdvancedSearchMode = (
  searchState: ComponentSearchViewState
): boolean => searchState.advancedSearchState !== null;

export const getValidAdvancedSearchQuery = (
  advancedSearchState: ComponentSearchViewState['advancedSearchState']
): QueryBuilderQuery | null =>
  advancedSearchState?.status === 'valid' ? advancedSearchState.query : null;

export const toSimpleSearchQuery = (
  componentName: string,
  componentType: string
): QueryBuilderQuery | null =>
  componentName || componentType
    ? advancedSearchComponentNameAndTypeAndStartSetQuery(
        componentName,
        componentType,
        null
      )
    : null;

const getTopLevelRules = (query: QueryBuilderQuery): QueryBuilderRule[] => {
  const subquery = queryBuilderOperations.getSubqueryFromQuery(query);
  if (subquery.condition !== BooleanOperator.AND) return [];
  return subquery.rules.filter(isQueryBuilderRule);
};

const findRuleValue = (
  rules: QueryBuilderRule[],
  field: string,
  operator: Operator
): string => {
  const rule = rules.find(
    candidate => candidate.field === field && candidate.operator === operator
  );
  return typeof rule?.value === 'string' ? rule.value : '';
};

export const toSimpleSearchFilters = (
  query: QueryBuilderQuery | null
): SimpleSearchFilters => {
  if (!query) return emptySimpleSearchFilters;

  const rules = getTopLevelRules(query);
  return {
    componentName: findRuleValue(rules, 'name', Operator.CONTAINS_SUBSTRING),
    componentType: findRuleValue(rules, 'typeName', Operator.EQUAL),
  };
};

export const toCarriedSearchState = (
  searchState: ComponentSearchViewState,
  mode: SelectContextSearchMode
): CarriedSearchState => {
  if (mode === 'advanced') {
    return {
      mode: 'advanced',
      query: toSimpleSearchQuery(
        searchState.componentName,
        searchState.componentType
      ),
    };
  }
  return {
    mode: 'simple',
    filters: toSimpleSearchFilters(
      getValidAdvancedSearchQuery(searchState.advancedSearchState)
    ),
  };
};
